import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Grid,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  SelectChangeEvent
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Numbers as NumbersIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

// Define types for better TypeScript support
interface MachineMapping {
  machineId: string;
  workerId: string;
  assetTagSnipeId: string | null;
}

interface UpdateResponse {
  machineId: string;
  status: 'OK' | 'ERROR';
  message?: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface BulkEditDialogProps {
  open: boolean;
  onClose: () => void;
  selectedMachines: string[];
  mappings: MachineMapping[];
  onBulkUpdate: (payload: Array<{ machineId: string; assetTagSnipeId: string | null }>) => Promise<void>;
  updating: boolean;
}

interface ApiResponse {
  content: MachineMapping[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Helper function to extract and increment numbers from strings
const extractNumberFromString = (str: string): number | null => {
  const match = str.match(/\d+$/);
  return match ? parseInt(match[0], 10) : null;
};

const generateSequentialTags = (baseTag: string, count: number, startIndex: number = 1): string[] => {
  const baseWithoutNumber = baseTag.replace(/\d+$/, '');
  return Array.from({ length: count }, (_, i) => {
    const number = startIndex + i;
    return `${baseWithoutNumber}${number}`;
  });
};

// Bulk Edit Dialog Component (Simplified)
function BulkEditDialog({ 
  open, 
  onClose, 
  selectedMachines, 
  mappings, 
  onBulkUpdate,
  updating 
}: BulkEditDialogProps) {
  const [sequentialTag, setSequentialTag] = useState<string>('');
  const [startNumber, setStartNumber] = useState<number>(1);
  const [individualTags, setIndividualTags] = useState<Record<string, string>>({});
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');

  useEffect(() => {
    if (open && selectedMachines.length > 0) {
      // Initialize individual tags with current values (should be empty for unconfigured)
      const initialIndividualTags: Record<string, string> = {};
      selectedMachines.forEach(machineId => {
        initialIndividualTags[machineId] = '';
      });
      setIndividualTags(initialIndividualTags);
      setSequentialTag('');
      setStartNumber(1);
      setValidationError('');
    }
  }, [open, selectedMachines]);

  const handleIndividualTagChange = (machineId: string, value: string) => {
    setIndividualTags(prev => ({
      ...prev,
      [machineId]: value
    }));
  };

  const generateSequentialPreview = (): string[] => {
    if (!sequentialTag.trim()) return [];
    const baseWithoutNumber = sequentialTag.replace(/\d+$/, '');
    return generateSequentialTags(baseWithoutNumber, selectedMachines.length, startNumber);
  };

  const applySequentialTags = () => {
    const tags = generateSequentialPreview();
    const updates: Record<string, string> = {};
    selectedMachines.forEach((machineId, index) => {
      updates[machineId] = tags[index];
    });
    setIndividualTags(updates);
  };

  const validateTags = (): string => {
    for (const machineId in individualTags) {
      const tag = individualTags[machineId];
      if (tag && tag.length > 255) {
        return `Tag "${tag}" exceeds 255 character limit`;
      }
    }
    return '';
  };

  const handleSubmit = async () => {
    const error = validateTags();
    if (error) {
      setValidationError(error);
      return;
    }

    const payload = selectedMachines.map(machineId => ({
      machineId,
      assetTagSnipeId: individualTags[machineId]?.trim() || null
    }));

    await onBulkUpdate(payload);
    if (!updating) {
      onClose();
    }
  };

  const sequentialPreview = generateSequentialPreview();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EditIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Configure Selected Machines ({selectedMachines.length} selected)
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Sequential Tags Section */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <NumbersIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="500">
                    Generate Sequential Tags
                  </Typography>
                </Stack>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Enter a base tag with a number at the end (e.g., RACK-A01-1).
                      The system will generate sequential tags for all selected machines.
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="Base Tag"
                      value={sequentialTag}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSequentialTag(e.target.value)}
                      placeholder="e.g., RACK-A01-1"
                      size="small"
                      helperText="Include a number at the end to use as starting point"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Start Number"
                      type="number"
                      value={startNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartNumber(parseInt(e.target.value) || 1)}
                      size="small"
                      InputProps={{ inputProps: { min: 1 } }}
                    />
                  </Grid>
                </Grid>
                
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={applySequentialTags}
                    disabled={!sequentialTag.trim()}
                    startIcon={<NumbersIcon />}
                  >
                    Generate Tags
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setPreviewVisible(!previewVisible)}
                    startIcon={previewVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  >
                    {previewVisible ? 'Hide Preview' : 'Show Preview'}
                  </Button>
                </Stack>
                
                {previewVisible && sequentialTag && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Preview of generated tags:
                    </Typography>
                    <Stack spacing={0.5}>
                      {sequentialPreview.map((tag, index) => (
                        <Typography key={index} variant="body2" fontFamily="monospace">
                          Machine {selectedMachines[index]}: {tag}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Individual Tags Editor */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="500">
                    Individual Tags
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedMachines.length} machine(s)
                  </Typography>
                </Stack>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Edit asset tags for each machine individually. Use the table below or edit directly in the main table.
                </Typography>
                
                <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell><strong>Machine ID</strong></TableCell>
                          <TableCell><strong>Worker ID</strong></TableCell>
                          <TableCell><strong>Asset Tag</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedMachines.map((machineId) => {
                          const machine = mappings.find(m => m.machineId === machineId);
                          return (
                            <TableRow key={machineId} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="500">
                                  {machineId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {machine?.workerId || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={individualTags[machineId] || ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleIndividualTagChange(machineId, e.target.value)}
                                  placeholder="Enter asset tag..."
                                  onKeyPress={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {validationError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {validationError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={updating ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={updating}
        >
          {updating ? 'Configuring...' : 'Configure Selected'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Confirmation Dialog Component
function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  machineId,
  tagValue,
  updating
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  machineId: string;
  tagValue: string;
  updating: boolean;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Confirm Asset Tag Assignment</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to assign the asset tag "<strong>{tagValue}</strong>" to Machine <strong>{machineId}</strong>?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={updating}
          startIcon={updating ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {updating ? 'Saving...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Update Tag Dialog Component
function UpdateTagDialog({
  open,
  onClose,
  onConfirm,
  machineId,
  currentTag,
  updating
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (newTag: string) => void;
  machineId: string;
  currentTag: string;
  updating: boolean;
}) {
  const [newTag, setNewTag] = useState<string>('');

  useEffect(() => {
    if (open) {
      setNewTag(currentTag || '');
    }
  }, [open, currentTag]);

  const handleSubmit = () => {
    if (newTag.trim()) {
      onConfirm(newTag.trim());
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Update Asset Tag</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Update asset tag for Machine <strong>{machineId}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="New Asset Tag"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Enter new asset tag..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={updating || !newTag.trim()}
          startIcon={updating ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {updating ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Main AssetController Component
export default function AssetController() {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [mappings, setMappings] = useState<MachineMapping[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [selectedMachines, setSelectedMachines] = useState<string[]>([]);
  const [bulkUpdateDialog, setBulkUpdateDialog] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
  const [updating, setUpdating] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // State for confirmation dialog
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    machineId: string;
    tagValue: string;
  }>({
    open: false,
    machineId: '',
    tagValue: ''
  });

  // State for update tag dialog
  const [updateTagDialog, setUpdateTagDialog] = useState<{
    open: boolean;
    machineId: string;
    currentTag: string;
  }>({
    open: false,
    machineId: '',
    currentTag: ''
  });

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit: rowsPerPage,
        key: searchKey || undefined,
      };

      if (activeTab === 0) {
        params.assetTagNull = true;
      }

      const response = await axios.get<ApiResponse>(
        `${API_PATH}/pool/worker-asset-mappings/list`,
        {
          ...getAxiosConfig(),
          params,
        }
      );

      setMappings(response.data.content);
      setTotalElements(response.data.totalElements);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      showSnackbar('Failed to load mappings', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMappings();
  }, [activeTab, page, rowsPerPage, searchKey]);

  const showSnackbar = (message: string, severity: SnackbarState['severity'] = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
    setSelectedMachines([]);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(event.target.value);
    setPage(0);
  };

  const handleSelectMachine = (machineId: string) => {
    setSelectedMachines(prev =>
      prev.includes(machineId)
        ? prev.filter(id => id !== machineId)
        : [...prev, machineId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMachines.length === mappings.length) {
      setSelectedMachines([]);
    } else {
      setSelectedMachines(mappings.map(m => m.machineId));
    }
  };

  const updateMachine = async (machineId: string, assetTagSnipeId: string | null) => {
    setUpdating(true);
    try {
      const payload = [{
        machineId,
        assetTagSnipeId: assetTagSnipeId?.trim() || null
      }];

      const response = await axios.post<UpdateResponse[]>(
        `${API_PATH}/pool/worker-asset-mappings/update`,
        payload,
        getAxiosConfig()
      );

      if (response.data[0]?.status === 'OK') {
        fetchMappings();
        showSnackbar(`Machine ${machineId} updated successfully`);
      } else {
        throw new Error(response.data[0]?.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating machine:', error);
      showSnackbar(`Failed to update machine ${machineId}: ${(error as Error).message}`, 'error');
    } finally {
      setUpdating(false);
      setConfirmationDialog({ open: false, machineId: '', tagValue: '' });
      setUpdateTagDialog({ open: false, machineId: '', currentTag: '' });
    }
  };

  const handleBulkUpdate = async (payload: Array<{ machineId: string; assetTagSnipeId: string | null }>) => {
    setUpdating(true);
    try {
      const response = await axios.post<UpdateResponse[]>(
        `${API_PATH}/pool/worker-asset-mappings/update`,
        payload,
        getAxiosConfig()
      );

      const errors = response.data.filter(item => item.status === 'ERROR');
      const successCount = response.data.length - errors.length;

      if (errors.length === 0) {
        showSnackbar(`Successfully configured ${successCount} machine(s)`);
        setSelectedMachines([]);
        fetchMappings();
      } else {
        showSnackbar(
          `Configured ${successCount} machines, ${errors.length} failed`,
          'warning'
        );
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      showSnackbar('Configuration failed', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleClearTag = (machineId: string) => {
    updateMachine(machineId, null);
  };

  const handleUpdateTag = (machineId: string, currentTag: string) => {
    setUpdateTagDialog({
      open: true,
      machineId,
      currentTag
    });
  };

  const confirmUpdateTag = (newTag: string) => {
    const { machineId } = updateTagDialog;
    updateMachine(machineId, newTag);
  };

  const renderStatusChip = (assetTag: string | null) => {
    if (assetTag) {
      return (
        <Tooltip title="Configured">
          <Chip
            label="Configured"
            color="success"
            size="small"
            icon={<CheckCircleIcon />}
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Unconfigured">
        <Chip
          label="Unconfigured"
          color="warning"
          size="small"
          icon={<ErrorIcon />}
        />
      </Tooltip>
    );
  };

  const handleSaveTag = (machineId: string, tagValue: string) => {
    if (tagValue.trim()) {
      setConfirmationDialog({
        open: true,
        machineId,
        tagValue: tagValue.trim()
      });
    }
  };

  const confirmSaveTag = () => {
    const { machineId, tagValue } = confirmationDialog;
    updateMachine(machineId, tagValue);
  };

  const unconfiguredCount = mappings.filter(m => !m.assetTagSnipeId).length;
  const configuredCount = mappings.filter(m => m.assetTagSnipeId).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        alignItems="center"
        position="sticky"
        top="70px"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
        gap={1}
        zIndex={100}
        sx={{
          mb: 2,
          backgroundColor: '#d1e8ff',
          padding: { xs: 1, sm: 1.5 },
          borderRadius: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'monospace',
            ml: { xs: 0, sm: '1rem' },
            color: '#1565c0',
            textAlign: { xs: 'center', sm: 'left' },
            width: '100%',
          }}
          gutterBottom
        >
          Asset Management
        </Typography>
      </Box>

      {/* Main Content Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{ px: 2, pt: 1 }}
            >
              <Tab 
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ErrorIcon fontSize="small" />
                    <span>Unconfigured</span>
                    {unconfiguredCount > 0 && (
                      <Chip label={unconfiguredCount} size="small" color="warning" />
                    )}
                  </Stack>
                } 
              />
              <Tab 
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckCircleIcon fontSize="small" />
                    <span>Configured</span>
                    {configuredCount > 0 && (
                      <Chip label={configuredCount} size="small" color="success" />
                    )}
                  </Stack>
                } 
              />
            </Tabs>
          </Box>

          {/* Search and Actions Bar */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                placeholder="Search by machine ID, asset tag, or worker ID..."
                value={searchKey}
                onChange={handleSearch}
                sx={{ flexGrow: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  inputRef: searchInputRef
                }}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchMappings}
                disabled={loading || updating}
              >
                Refresh
              </Button>
            </Stack>
          </Box>

          {/* Batch Edit Actions - Only show for unconfigured tab */}
          {selectedMachines.length > 0 && activeTab === 0 && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 0 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="primary.contrastText">
                  {selectedMachines.length} machine(s) selected
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => setBulkUpdateDialog(true)}
                    disabled={updating}
                    startIcon={<EditIcon />}
                  >
                    Configure Selected
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* Table */}
          <Box sx={{ position: 'relative', minHeight: 400 }}>
            {loading && <LinearProgress />}
            
            {!loading && (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        {activeTab === 0 && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedMachines.length > 0 && selectedMachines.length < mappings.length}
                              checked={mappings.length > 0 && selectedMachines.length === mappings.length}
                              onChange={handleSelectAll}
                            />
                          </TableCell>
                        )}
                        {activeTab === 1 && (
                          <TableCell padding="checkbox" sx={{ width: '60px' }}></TableCell>
                        )}
                        <TableCell><strong>Machine ID</strong></TableCell>
                        <TableCell><strong>Worker ID</strong></TableCell>
                        <TableCell><strong>Asset Tag</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mappings.map((mapping) => (
                        <TableRow 
                          key={mapping.machineId} 
                          hover
                        >
                          {activeTab === 0 && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedMachines.includes(mapping.machineId)}
                                onChange={() => handleSelectMachine(mapping.machineId)}
                              />
                            </TableCell>
                          )}
                          {activeTab === 1 && (
                            <TableCell padding="checkbox" sx={{ width: '60px' }}></TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {mapping.machineId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {mapping.workerId || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {mapping.assetTagSnipeId ? (
                              <Chip
                                label={mapping.assetTagSnipeId}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                  size="small"
                                  placeholder="Enter asset tag..."
                                  onKeyPress={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                      handleSaveTag(mapping.machineId, (e.target as HTMLInputElement).value);
                                    }
                                  }}
                                  sx={{ width: 200 }}
                                />
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            {renderStatusChip(mapping.assetTagSnipeId)}
                          </TableCell>
                          <TableCell>
                            {mapping.assetTagSnipeId ? (
                              <Tooltip title="Update asset tag">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleUpdateTag(mapping.machineId, mapping.assetTagSnipeId || '')}
                                  disabled={updating}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Save tag">
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    const inputField = (e.target as HTMLElement).closest('tr')?.querySelector('input[type="text"]') as HTMLInputElement;
                                    const tag = inputField?.value;
                                    if (tag) handleSaveTag(mapping.machineId, tag);
                                  }}
                                  disabled={updating}
                                  startIcon={<SaveIcon />}
                                >
                                  Save
                                </Button>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {mappings.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={activeTab === 0 ? 6 : 5} align="center" sx={{ py: 4 }}>
                            <Box sx={{ py: 4 }}>
                              <SearchIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                              <Typography color="text.secondary">
                                No machines found
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Showing {mappings.length} of {totalElements} machines
                    </Typography>
                    <TablePagination
                      component="div"
                      count={totalElements}
                      page={page}
                      onPageChange={(event: unknown, newPage: number) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      rowsPerPageOptions={[25, 50, 100]}
                      labelRowsPerPage="Rows per page:"
                    />
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Bulk Edit Dialog - Only show for unconfigured tab */}
      {activeTab === 0 && (
        <BulkEditDialog
          open={bulkUpdateDialog}
          onClose={() => setBulkUpdateDialog(false)}
          selectedMachines={selectedMachines}
          mappings={mappings}
          onBulkUpdate={handleBulkUpdate}
          updating={updating}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog({ open: false, machineId: '', tagValue: '' })}
        onConfirm={confirmSaveTag}
        machineId={confirmationDialog.machineId}
        tagValue={confirmationDialog.tagValue}
        updating={updating}
      />

      {/* Update Tag Dialog */}
      <UpdateTagDialog
        open={updateTagDialog.open}
        onClose={() => setUpdateTagDialog({ open: false, machineId: '', currentTag: '' })}
        onConfirm={confirmUpdateTag}
        machineId={updateTagDialog.machineId}
        currentTag={updateTagDialog.currentTag}
        updating={updating}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}